// Consenso ai cookie (Garante privacy, linee guida del 10/06/2021).
// Due categorie facoltative, entrambe spente finché l'utente non sceglie:
//   - statistiche: Google Analytics 4 (G-SH5QJM52S0), caricato SOLO dopo il consenso;
//   - mappe: la mappa interattiva di Google Maps nella pagina Contatti.
// La scelta resta nel browser (localStorage) per 6 mesi, poi il banner si ripresenta.
// Se cambiano i servizi di terze parti, alzare VERSION: il banner ricompare a tutti.
// Testi collegati: src/routes/cookie.tsx e src/routes/privacy.tsx.

export const GA_ID = "G-SH5QJM52S0";

const KEY = "bottega-consenso-cookie";
const VERSION = 1;
const DURATA_MS = 182 * 24 * 60 * 60 * 1000; // circa 6 mesi

export const EVENTO_CONSENSO = "bottega:consenso";
export const EVENTO_APRI_PREFERENZE = "bottega:preferenze-cookie";

export type Scelte = { statistiche: boolean; mappe: boolean };
type Salvato = Scelte & { v: number; data: string };

export function leggiConsenso(): Scelte | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Salvato;
    if (s.v !== VERSION) return null;
    const quando = Date.parse(s.data);
    if (!Number.isFinite(quando) || Date.now() - quando > DURATA_MS) return null;
    return { statistiche: s.statistiche === true, mappe: s.mappe === true };
  } catch {
    return null;
  }
}

export function salvaConsenso(scelte: Scelte) {
  const prima = leggiConsenso();
  const s: Salvato = { ...scelte, v: VERSION, data: new Date().toISOString() };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // localStorage non disponibile (navigazione privata rigida): vale per questa visita.
  }
  window.dispatchEvent(new CustomEvent<Scelte>(EVENTO_CONSENSO, { detail: scelte }));
  if (scelte.statistiche) {
    caricaAnalytics();
  } else if (prima?.statistiche) {
    revocaAnalytics();
  }
}

export function apriPreferenzeCookie() {
  window.dispatchEvent(new Event(EVENTO_APRI_PREFERENZE));
}

type Gtag = (...args: unknown[]) => void;
type WinGa = Window & { dataLayer?: unknown[]; gtag?: Gtag } & Record<string, unknown>;

let gaCaricato = false;

// Lo stub di gtag con il Consent Mode «denied» è già nell'head (src/routes/__root.tsx):
// qui si passa a «granted» solo per le statistiche e si carica la libreria.
export function caricaAnalytics() {
  if (typeof window === "undefined") return;
  const w = window as unknown as WinGa;
  w[`ga-disable-${GA_ID}`] = false;
  w.dataLayer = w.dataLayer || [];
  const gtag: Gtag = w.gtag || function () {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments);
  };
  w.gtag = gtag;
  gtag("consent", "update", { analytics_storage: "granted" });
  if (gaCaricato) return;
  gaCaricato = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  gtag("config", GA_ID);
}

// Revoca: blocca subito l'invio di altri dati e cancella i cookie di Google Analytics
// (_ga, _ga_<id>) sia sul dominio sia sul dominio padre, dove li scrive GA.
function revocaAnalytics() {
  const w = window as unknown as WinGa;
  w[`ga-disable-${GA_ID}`] = true;
  w.gtag?.("consent", "update", { analytics_storage: "denied" });
  const host = window.location.hostname;
  const domini = ["", host, `.${host}`, `.${host.replace(/^www\./, "")}`];
  document.cookie.split(";").forEach((c) => {
    const nome = c.split("=")[0].trim();
    if (!nome.startsWith("_ga")) return;
    domini.forEach((d) => {
      document.cookie = `${nome}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
}
